// Table data model — shared by the table operations (table-ops) and the
// TableElement React component.

import type {ClientFillableConfig} from './client-fillable';
import type {PhotoBoxConfig} from './photo-box';
import type {TextMark} from './rich-text';
import type {
  CheckboxControlConfig,
  LogoImageConfig,
  SignatureControlConfig,
  SizedControlConfig,
  TextControlConfig,
} from './control-config';

// ─── Constants ────────────────────────────────────────────────────────────────

export const MIN_COL_WIDTH = 40;
/** A row height is a *minimum* per HTML table semantics, so the content is the real
 *  floor and the browser enforces it — nothing is ever clipped (DERP-226, Canva-style
 *  resize). This only keeps a requested height off zero and out of the negatives. */
export const MIN_ROW_HEIGHT = 1;
export const DEFAULT_COL_WIDTH = 120;
export const DEFAULT_ROW_HEIGHT = 36;
/** Height an unsized image input inside a cell renders at, in px. Its width has no
 *  equivalent default: an unsized cell image stretches to the column (`100%`). */
export const CELL_IMAGE_DEFAULT_HEIGHT = 80;

/**
 * How tall an unsized signature cell is drawn.
 *
 * Was 48, written out separately in three places -- the editor's cell view, the
 * fill adapter and the unfillable placeholder -- which is how they came to be
 * the same number by coincidence rather than by construction. Unpicking them is
 * what made raising it possible at all.
 *
 * 80px is about 28mm on A4. 48 was enough of a rule to sign ON and not enough
 * of a box to sign IN with a fingertip, which is how these are actually signed:
 * a phone, on site, with a thumb. The extra 32px cost the page almost nothing
 * and is the difference between a signature and a scribble.
 *
 * An author who wants a different one drags the element; this is only the
 * starting point.
 */
export const CELL_SIGNATURE_DEFAULT_HEIGHT = 80;

// ─── Types ────────────────────────────────────────────────────────────────────

export type BorderLineStyle = 'solid' | 'dashed' | 'dotted' | 'double';

export interface TableCellStyle {
  fontFamily?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  textAlign?: 'left' | 'center' | 'right';
  verticalAlign?: 'top' | 'middle' | 'bottom';
  backgroundColor?: string;
  padding?: number;
  // Per-side border widths: undefined = inherit default table border, 0 = no border, n = n px
  borderTopWidth?: number;
  borderRightWidth?: number;
  borderBottomWidth?: number;
  borderLeftWidth?: number;
  // Per-side line style. Falls back to the cell-level `borderStyle` (legacy) when unset.
  borderTopStyle?: BorderLineStyle;
  borderRightStyle?: BorderLineStyle;
  borderBottomStyle?: BorderLineStyle;
  borderLeftStyle?: BorderLineStyle;
  borderColor?: string;
  /** Cell-level fallback line style for sides without an explicit per-side style. */
  borderStyle?: BorderLineStyle;
}

// ─── Cell elements ───────────────────────────────────────────────────────────
// A cell can host a typed mini-element (heading / input / photo / …). Each type's
// config is strictly typed and discriminated on `type` — mirrors the top-level
// `PhotoRequirement` union so renderers/config panels narrow `config` instead of
// hand-casting it. (These are the in-table analogues of the standalone element
// configs; they intentionally carry only the subset of fields the table uses.)

/** Headings carry their text in `label`; the config holds only the styles on part
 *  of it, over the cell's own text style (DERP-307) — see `rich-text`. */
export interface CellHeadingConfig {
  label_marks?: TextMark[];
}

export type CellTextConfig = ClientFillableConfig & TextControlConfig & {required?: boolean};

/** An image input inside a table cell. The box and its overlay are
 *  `PhotoBoxConfig`, shared with the standalone photo slot — see there. */
export interface CellPhotoConfig extends ClientFillableConfig, PhotoBoxConfig {
  required?: boolean;
}

export type CellLogoConfig = LogoImageConfig;

export type CellCheckboxConfig = ClientFillableConfig &
  CheckboxControlConfig & {required?: boolean};

export interface CellDropdownConfig extends ClientFillableConfig, SizedControlConfig {
  options?: string[];
  default_value?: string;
  lock_value?: boolean;
  required?: boolean;
}

export type CellSignatureConfig = ClientFillableConfig & SignatureControlConfig;

interface CellElementBase {
  label: string;
  /**
   * Where this element sits across its cell — placement, not typography.
   *
   * On the base rather than in each type's config because it answers the same
   * question for all of them, and because it is the ELEMENT's own answer:
   * `TableCellStyle.textAlign` is the cell's, and stands as the default for
   * every element in the stack (see `cellItemStyle`). This overrides it for one
   * element, which is what an author pointing at a single element in a cell is
   * asking to do.
   *
   * A `logo` is the exception and keeps `CellLogoConfig.align`. That control
   * serves standalone logos too, where there is no cell to inherit from, so the
   * field has to live somewhere a standalone element can carry it. One field per
   * surface beats two fields for one concept.
   *
   * Absent means "follow the cell", which is every element authored so far.
   */
  align?: 'left' | 'center' | 'right';
}

export type CellElement =
  | (CellElementBase & {type: 'heading'; config: CellHeadingConfig})
  | (CellElementBase & {type: 'text'; config: CellTextConfig})
  | (CellElementBase & {type: 'photo'; config: CellPhotoConfig})
  | (CellElementBase & {type: 'logo'; config: CellLogoConfig})
  | (CellElementBase & {type: 'checkbox'; config: CellCheckboxConfig})
  | (CellElementBase & {type: 'dropdown'; config: CellDropdownConfig})
  | (CellElementBase & {type: 'signature'; config: CellSignatureConfig});

/** The set of cell-element types (discriminant values). */
export type CellElementType = CellElement['type'];

export interface TableCellData {
  id: string;
  content: string;
  /** Styles on part of `content`, over the cell's own `style` (DERP-307). */
  content_marks?: TextMark[];
  element?: CellElement;
  elements?: CellElement[];
  rowSpan?: number;
  colSpan?: number;
  hidden?: boolean;
  style?: TableCellStyle;
}

export interface TableRowData {
  id: string;
  isHeader?: boolean;
  height?: number;
  cells: TableCellData[];
}

export interface TableColumnData {
  id: string;
  width?: number;
  minWidth?: number;
}

export interface CellRange {
  startRow: number;
  startCol: number;
  endRow: number;
  endCol: number;
}

/**
 * Where a table sits across the page.
 *
 * Only meaningful for a table in FLOW: an absolute one is placed by its own
 * `x`, so "align it in the page" is a question its position has already
 * answered. Unset reads as `left`, which is where a table has always sat.
 */
export type TableAlign = 'left' | 'center' | 'right';

export interface TableConfig {
  rows: TableRowData[];
  columns: TableColumnData[];
  cellRange?: CellRange | null;
  tableWidth?: number;
  tableAlign?: TableAlign;
  tableHeight?: number;
  selectedElementIndex?: number | null;
}

/**
 * The subset of a `TableConfig` that `TableElement`'s latest-value ref holds, so
 * event handlers, rAF callbacks and native listeners read current state without
 * re-subscribing. Built only by `toTableSnapshot` — one builder, so the
 * post-render sync and a mid-tick commit cannot project it differently.
 */
export interface TableConfigSnapshot {
  rows: TableRowData[];
  columns: TableColumnData[];
  cellRange: CellRange | null;
  tableWidth?: number;
  // Carried, or a drag would erase it: every resize rebuilds the committed
  // config from this snapshot, so a field missing here survives exactly until
  // the next column is dragged.
  tableAlign?: TableAlign;
  selectedElementIndex: number | null;
}

/**
 * The margins that put a flow table left, centre or right on the page.
 *
 * `undefined` for an absolute table and for `left`: left IS the default, so
 * writing `marginRight: auto` for it would be a style that changes nothing,
 * and a style that changes nothing is one the next reader has to prove
 * harmless.
 */
export function tableAlignStyle(
  align: TableAlign | undefined,
  isAbsolute: boolean,
): {marginLeft?: string; marginRight?: string} {
  if (isAbsolute || !align || align === 'left') return {};
  if (align === 'center') return {marginLeft: 'auto', marginRight: 'auto'};
  return {marginLeft: 'auto'};
}

/**
 * The wrapper box a table renders into: how wide it is, and where it sits.
 *
 * The in-drag draft wins over the stored width, which wins over "fill the
 * column" — one ordering, stated once, rather than a ternary chain inline in the
 * markup beside an unrelated margin rule.
 */
export function tableWrapperStyle(
  cfg: Pick<TableConfig, 'tableWidth' | 'tableAlign'>,
  draftWidth: number | null,
  isAbsolute: boolean,
): React.CSSProperties {
  return {
    width: draftWidth != null ? `${draftWidth}px` : cfg.tableWidth ? `${cfg.tableWidth}px` : '100%',
    ...tableAlignStyle(cfg.tableAlign, isAbsolute),
  };
}
